import type { Response } from 'lib-shared-legacy-types';
import { v4 as uuidv4 } from 'uuid';

import { ajaxSend, ApiBasePath } from 'api/api';
import { getSocketOrWorker } from 'app/middleware/websocket/socketInterface';
import type { AudioT } from 'components/shared/audio/api/types';

import { AnnouncementItem } from './announcementAdapter';

import { Adapter, Item } from './index';

export interface WaitMusicItem {
  id: string;
  item: AudioT;
}

type GetItemsResponse = Response<{
  wait_music: AudioT[];
}>;

type UploadItemResponse = Response<{
  relative_uri: string;
}>;

export default class WaitMusicAdapter implements Adapter {
  private _items: WaitMusicItem[] = [];

  private mapWaitMusicToItem({
    uuid,
    name,
    original_as_mp3_url,
  }: AudioT): Item {
    return {
      id: uuid || uuidv4(),
      name: name || '',
      url: original_as_mp3_url || '',
    };
  }

  getItemForCallback(id: string): AudioT | AnnouncementItem | undefined {
    const items = this._items;
    const item = items.find((item) => item.id === id);

    if (!item) {
      return undefined;
    }

    const {
      item: { uuid, ...audio },
    } = item;

    return {
      ...audio,
      uuid,
      id: uuid,
    };
  }

  getItemFromURL(url: string): Item | undefined {
    const items = this._items;
    const { item } =
      items.find(
        ({ item: { master_file_path } }) => url === master_file_path,
      ) || {};

    return item && this.mapWaitMusicToItem(item);
  }

  getFormattedItem(audio: AudioT | AnnouncementItem): Item | undefined {
    const { uuid: id } = audio as AudioT;

    const items = this._items;
    const { item } = items.find((item) => item.id === id) || {};

    if (!item) {
      return undefined;
    }

    return this.mapWaitMusicToItem(item);
  }

  async getItems(): Promise<Item[]> {
    const {
      data: { wait_music },
    } = await ajaxSend<GetItemsResponse>({
      path: '/v1/wait_music',
    });

    const items = wait_music.map<Item>(this.mapWaitMusicToItem);

    this._items = items.map(({ id }, index) => ({
      id,
      item: wait_music[index],
    }));

    return items;
  }

  async upload(file: File): Promise<string> {
    const data = new FormData();

    data.append('file', file);

    const {
      data: { relative_uri },
    } = await ajaxSend<UploadItemResponse>({
      path: '/private/attachment',
      method: 'POST',
      basePath: ApiBasePath.Files,
      headers: {
        contentType: 'multipart/form-data',
      },
      data,
    });

    await ajaxSend({
      path: '/v1/wait_music',
      method: 'POST',
      data: {
        name: file.name,
        master_file_path: relative_uri,
      },
    });

    /*
     * /v1/wait_music only queues the upload.
     * We need to listen for a web socket message to know when it's actually uploaded.
     */

    const socketOrWorker = await getSocketOrWorker();

    await socketOrWorker.waitForMessage('flow:upload_wait_music_result');

    return relative_uri;
  }
}
